// useTheaterMode — global toggle that hides Header, BottomNav, and
// Category Chip Rail so the in-app player gets the full viewport.
//
// Why a custom event + module-level state (no React Context):
// 1. The players sit deep inside their own modal trees; threading a
//    Context provider through every page would touch ~60 files.
// 2. Header / BottomNav / ChipRail re-mount on every route change;
//    they need to read the LATEST state on mount, not stale closure.
// 3. ESC + the F key listener must work from anywhere (modal, body),
//    so we bind globally rather than per-component.
//
// API:
//   const { theaterMode, toggle, enter, exit } = useTheaterMode();
//
// Effects:
//   - Sets `data-theater="on" | "off"` on <html> so Tailwind variants
//     and plain CSS can style around it.
//   - F key toggles, Esc exits (only when theater mode is ON, so we
//     don't fight other ESC handlers like modal-close).
//   - Auto-exits when no <video>/<iframe>/<audio> element is on the
//     page anymore (e.g. user navigated away from a player). This
//     prevents the user getting stuck in a chrome-less app.
import { useCallback, useEffect, useState } from "react";

const EVENT_NAME = "hw:theater-mode";
let CURRENT = false;

function setGlobal(next) {
  CURRENT = !!next;
  try {
    document.documentElement.setAttribute("data-theater", CURRENT ? "on" : "off");
  } catch (_) { /* SSR */ }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: CURRENT }));
  } catch (_) { /* old browsers */ }
}

export default function useTheaterMode() {
  const [theaterMode, setTheaterMode] = useState(CURRENT);

  useEffect(() => {
    const onChange = (e) => setTheaterMode(!!e.detail);
    window.addEventListener(EVENT_NAME, onChange);
    return () => window.removeEventListener(EVENT_NAME, onChange);
  }, []);

  // Keyboard shortcuts. F toggles, Esc exits — but ONLY when theater
  // mode is currently on, so we don't interfere with modal-close ESC
  // handlers when nothing's in theater mode.
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in an input/textarea/contenteditable.
      const tgt = e.target;
      if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) {
        return;
      }
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        setGlobal(!CURRENT);
      } else if (e.key === "Escape" && CURRENT) {
        setGlobal(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const enter = useCallback(() => setGlobal(true), []);
  const exit = useCallback(() => setGlobal(false), []);
  const toggle = useCallback(() => setGlobal(!CURRENT), []);

  return { theaterMode, enter, exit, toggle };
}
